// Página "Minhas Matérias" — lista disciplinas do aluno, mostra saldo
// de créditos por matéria, e permite comprar pacotes (gera QR Pix).
// Também mostra "Pagamentos pendentes" no topo se houver.

import { signal } from "@preact/signals";
import { useEffect, useState } from "preact/hooks";
import {
  AlertCircle,
  Clock,
  CreditCard,
  GraduationCap,
  BookOpen,
  Loader2,
  ShoppingBag
} from "lucide-react";
import {
  api,
  ApiError,
  type StudentDisciplinaRow,
  type CompraRow,
  type PacoteAvailable
} from "./api";
import { CompraPacoteModal, openCompraPacote } from "./CompraPacoteModal";
import { showCompraPendente } from "./CompraPendenteModal";
import { DisciplinaDetailModal } from "./DisciplinaDetailModal";

const phase = signal<"loading" | "ready" | "error">("loading");
const errMsg = signal<string | null>(null);
const materias = signal<StudentDisciplinaRow[]>([]);
const pendentes = signal<CompraRow[]>([]);
// saldo por offeringId
const saldos = signal<Map<string, number>>(new Map());
// avaliações por disciplinaId — finalScore + status + progresso (items com nota / total)
const avaliacoes = signal<
  Map<
    string,
    {
      finalScore: number;
      status: "aprovado" | "recuperacao" | "reprovado" | "incompleto";
      passingGrade: number;
      itemsAvaliados: number;
      itemsTotal: number;
    }
  >
>(new Map());

async function load() {
  phase.value = "loading";
  errMsg.value = null;
  try {
    const [disc, comprasResp] = await Promise.all([
      api.listMyDisciplinas(),
      api.listMyCompras("pending")
    ]);
    materias.value = disc.disciplinas;
    pendentes.value = comprasResp.compras;
    // Pré-carrega saldo de cada offering (paralelo, melhor UX)
    const newSaldos = new Map<string, number>();
    await Promise.all(
      disc.disciplinas.map(async (d) => {
        if (!d.offering) return;
        try {
          const r = await api.listPacotesDisciplina(d.id, d.offering.id);
          if (r.saldo != null) newSaldos.set(d.offering.id, r.saldo);
        } catch {
          // ignora — falha silenciosa
        }
      })
    );
    saldos.value = newSaldos;

    // Pré-carrega avaliação composta de cada disciplina (em paralelo).
    // Erros silenciam — disciplinas sem avaliação simplesmente não aparecem aqui.
    const newAval = new Map<
      string,
      {
        finalScore: number;
        status: "aprovado" | "recuperacao" | "reprovado" | "incompleto";
        passingGrade: number;
        itemsAvaliados: number;
        itemsTotal: number;
      }
    >();
    await Promise.all(
      disc.disciplinas.map(async (d) => {
        try {
          const r = await api.getDisciplinaEvaluation(d.id);
          if (r.items.length === 0) return;
          const itemsAvaliados = r.items.filter((it) => it.score != null).length;
          newAval.set(d.id, {
            finalScore: r.finalScore,
            status: r.status,
            passingGrade: r.disciplina.passingGrade,
            itemsAvaliados,
            itemsTotal: r.items.length
          });
        } catch {
          // ignora silenciosamente
        }
      })
    );
    avaliacoes.value = newAval;

    phase.value = "ready";
  } catch (e) {
    if (e instanceof ApiError && (e.status === 401 || e.status === 403)) {
      window.location.href = "/auth/";
      return;
    }
    errMsg.value = e instanceof Error ? e.message : String(e);
    phase.value = "error";
  }
}

function formatBRL(cents: number): string {
  return (cents / 100).toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL"
  });
}

export function MateriasView() {
  const [openDetail, setOpenDetail] = useState<string | null>(null);

  useEffect(() => {
    load();
  }, []);

  if (phase.value === "loading") {
    return (
      <div class="space-y-5">
        <header>
          <h1 class="flex items-center gap-2 text-2xl font-bold tracking-tight">
          <BookOpen className="size-6 text-primary" />
          Minhas Matérias
        </h1>
        </header>
        <p class="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="size-4 animate-spin" /> Carregando…
        </p>
      </div>
    );
  }

  if (phase.value === "error") {
    return (
      <div class="space-y-5">
        <header>
          <h1 class="flex items-center gap-2 text-2xl font-bold tracking-tight">
          <BookOpen className="size-6 text-primary" />
          Minhas Matérias
        </h1>
        </header>
        <div class="rounded-md border border-destructive/30 bg-destructive/5 p-4">
          <p class="flex items-center gap-2 text-sm text-destructive">
            <AlertCircle className="size-4" />
            {errMsg.value}
          </p>
        </div>
      </div>
    );
  }

  const list = materias.value;
  const pend = pendentes.value;

  return (
    <div class="space-y-5">
      <header>
        <h1 class="flex items-center gap-2 text-2xl font-bold tracking-tight">
          <BookOpen className="size-6 text-primary" />
          Minhas Matérias
        </h1>
        <p class="mt-1 text-sm text-muted-foreground">
          Suas matérias, saldo de créditos e histórico de pacotes.
        </p>
      </header>

      {pend.length > 0 && (
        <section class="rounded-md border border-amber-500/40 bg-amber-500/5 p-4">
          <div class="mb-2 flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-amber-700 dark:text-amber-400">
            <Clock className="size-4" />
            Pagamentos pendentes ({pend.length})
          </div>
          <ul class="space-y-1.5">
            {pend.map((c) => (
              <li key={c.id} class="flex flex-wrap items-center gap-2 rounded-md border border-border bg-background/60 px-3 py-2">
                <div class="min-w-0 flex-1">
                  <div class="text-sm font-semibold">{c.pacoteNomeSnap}</div>
                  <div class="text-[11px] text-muted-foreground">
                    {c.numAulasSnap} aulas · {formatBRL(c.valorCents)} · criado em{" "}
                    {new Date(c.createdAt).toLocaleString("pt-BR")}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => showCompraPendente(c)}
                  class="rounded-md border border-amber-500/50 bg-amber-500/10 px-3 py-1 text-xs font-semibold text-amber-700 hover:bg-amber-500/20 dark:text-amber-400"
                >
                  Ver QR Pix
                </button>
              </li>
            ))}
          </ul>
        </section>
      )}

      {list.length === 0 ? (
        <div class="rounded-md border border-dashed border-border bg-card/40 p-10 text-center">
          <GraduationCap className="mx-auto mb-3 size-8 text-muted-foreground/60" />
          <p class="text-sm text-muted-foreground">
            Você ainda não está matriculado em nenhuma matéria.
          </p>
          <p class="mt-1 text-xs text-muted-foreground/70">
            Aguarde o professor adicionar você à turma.
          </p>
        </div>
      ) : (
        <div class="grid gap-3 md:grid-cols-2">
          {list.map((m) => {
            const saldo = m.offering ? saldos.value.get(m.offering.id) ?? null : null;
            const aval = avaliacoes.value.get(m.id) ?? null;
            return (
              <article
                key={m.id}
                onClick={() => setOpenDetail(m.id)}
                class="cursor-pointer rounded-md border border-border bg-card/60 p-4 transition-colors hover:border-primary/40 hover:bg-accent/30"
              >
                <header class="mb-2 flex items-start gap-3">
                  <div class="grid size-10 shrink-0 place-items-center rounded-md bg-primary/10 text-primary ring-1 ring-primary/20">
                    <GraduationCap className="size-5" />
                  </div>
                  <div class="min-w-0 flex-1">
                    <h2 class="truncate text-base font-bold">{m.name}</h2>
                    {m.offering?.turmaName && (
                      <p class="text-[11px] text-muted-foreground">
                        Turma: {m.offering.turmaName}
                      </p>
                    )}
                  </div>
                </header>
                {m.teachers && m.teachers.length > 0 && (
                  <p class="text-xs text-muted-foreground">
                    Com{" "}
                    {m.teachers
                      .map((t) => t.name)
                      .join(", ")
                      .slice(0, 80)}
                  </p>
                )}

                {/* Nota em destaque (quando tem avaliação composta) */}
                {aval && <NotaPill aval={aval} />}

                <div class="mt-3 flex items-center justify-between rounded-md border border-primary/30 bg-primary/5 px-3 py-2">
                  <div class="flex items-center gap-1.5 text-[11px] font-semibold text-primary">
                    <CreditCard className="size-3.5" />
                    Saldo
                  </div>
                  <div class="text-base font-bold text-foreground">
                    {saldo == null ? "—" : `${saldo} aula${saldo === 1 ? "" : "s"}`}
                  </div>
                </div>
                {m.offering && (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      openCompraPacote({
                        disciplinaId: m.id,
                        disciplinaName: m.name,
                        offeringId: m.offering!.id
                      });
                    }}
                    class="mt-3 inline-flex w-full items-center justify-center gap-1.5 rounded-md border border-primary bg-primary px-3 py-2 text-xs font-semibold text-primary-foreground hover:brightness-110"
                  >
                    <ShoppingBag className="size-3.5" />
                    Comprar pacote
                  </button>
                )}
              </article>
            );
          })}
        </div>
      )}

      <CompraPacoteModal onCompraCreated={() => load()} />

      {openDetail && (
        <DisciplinaDetailModal
          disciplinaId={openDetail}
          onClose={() => setOpenDetail(null)}
        />
      )}
    </div>
  );
}

// ============================================================================
// NotaPill — bloco com nota final + status + progresso de itens avaliados.
// Aparece no card só quando a disciplina tem avaliação composta cadastrada.
// ============================================================================

function NotaPill({
  aval
}: {
  aval: {
    finalScore: number;
    status: "aprovado" | "recuperacao" | "reprovado" | "incompleto";
    passingGrade: number;
    itemsAvaliados: number;
    itemsTotal: number;
  };
}) {
  const tone =
    aval.status === "aprovado"
      ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300"
      : aval.status === "recuperacao"
        ? "border-amber-500/40 bg-amber-500/10 text-amber-700 dark:text-amber-300"
        : aval.status === "reprovado"
          ? "border-red-500/40 bg-red-500/10 text-red-700 dark:text-red-300"
          : "border-border bg-card/40 text-foreground";

  const statusLabel =
    aval.status === "aprovado"
      ? "Aprovado"
      : aval.status === "recuperacao"
        ? "Recuperação"
        : aval.status === "reprovado"
          ? "Reprovado"
          : "Em andamento";

  const pct = Math.min(100, Math.round((aval.finalScore / 10) * 100));
  const itemsPct =
    aval.itemsTotal > 0 ? Math.round((aval.itemsAvaliados / aval.itemsTotal) * 100) : 0;

  return (
    <div class={`mt-3 rounded-md border px-3 py-2 ${tone}`}>
      <div class="flex items-baseline justify-between gap-2">
        <div class="flex items-baseline gap-1.5">
          <span class="text-[10px] font-bold uppercase tracking-wider opacity-80">
            Nota atual
          </span>
          <span class="text-xl font-extrabold tabular-nums">
            {aval.finalScore.toFixed(2).replace(".", ",")}
          </span>
          <span class="text-[11px] font-semibold opacity-70">/ 10,00</span>
        </div>
        <span class="rounded-md bg-background/40 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider">
          {statusLabel}
        </span>
      </div>
      {/* Barra de progresso da nota (com marca da nota de corte) */}
      <div class="relative mt-1.5 h-1.5 overflow-hidden rounded-full bg-background/40">
        <div
          class="absolute left-0 top-0 h-full bg-current opacity-70"
          style={`width:${pct}%`}
        />
        {/* Marca da nota de corte */}
        <div
          class="absolute top-0 h-full w-px bg-foreground/50"
          style={`left:${Math.min(100, Math.round((aval.passingGrade / 10) * 100))}%`}
          title={`Nota de corte: ${aval.passingGrade.toFixed(2).replace(".", ",")}`}
        />
      </div>
      <div class="mt-1.5 flex items-center justify-between gap-2 text-[10px] opacity-80">
        <span>
          {aval.itemsAvaliados}/{aval.itemsTotal} avaliações concluídas
        </span>
        <span class="tabular-nums">
          corte {aval.passingGrade.toFixed(2).replace(".", ",")} · {itemsPct}% do peso
        </span>
      </div>
    </div>
  );
}
